/**
 * Moderation's words (ADR-056's amendment of 2026-10-04, items 9 and 10): what a player a GM acted on is shown, what
 * a GM's commands and window answer, the page's own sentences for a request that got no usable answer, and the
 * sentences the gateway's moderation routes answer with. Drafts for the author (ADR-061's amendment "the export is
 * a visual reference"): sentence case, a full stop after a sentence and none after a title, a button or a label. A
 * reason is a GM's own words, shown as they were typed, so a sentence that ends on one adds no full stop. Nothing
 * here names the GM to the player, and nothing promises an appeal.
 *
 * The list of who is online (ADR-056's amendment of 2026-10-06, decision 7) is worded under `online`; the mark
 * The staff badge of a listed player and of a family's read is worded under `badge` (ADR-112 decision 4.2; the amendment of
 * 2026-10-06, item 3); the mark "Staff" stays for a page whose gateway sends no badge. The staff's note about a family is
 * worded "Flag" wherever a GM reads it (decision 16), while its stored name stays `alert`: the keys here are the
 * stored names, so a row of any age reads "Flag".
 *
 * `refused` holds one sentence per refusal code of the routes: the gateway sends it as `message`, and the page
 * words the same key from `error` for the codes that have one sentence. `refusedFor` holds the sentences the
 * gateway chooses by the action asked for, which the page shows as they come. `route` holds the routes' other
 * answers; `{methods}` is a method's name, or two joined by `either`.
 *
 * `desk.done.lift` and `window.lengths.days` choose their form by `count`. `{purpose}` in the two `To` picks is
 * `window.pickFeedback`. `window.confirmBoth` joins two first names for `{firsts}`. `{header}` in `online.here` is
 * a map's name, or `online.grouped`. `{until}` in `window.leftUntil` is `window.until`. `window.quoted` puts quotation marks around what a
 * player reads, word for word. The two `Confirm` labels
 * of `window.go` are the confirmation's button, which opens nothing further and so ends in no ellipsis.
 */
export const MODERATION = {
  notice: {
    warnTitle: "A moderator warned you",
    warnSentence: "Reason: {reason}",
    warnAction: "I understand",
    kickTitle: "Removed from the world",
    kickSentence: "A moderator removed you from the world. You may return. Reason: {reason}",
    freezeTitle: "You are frozen",
    freezeSentence: "A moderator has paused your character while they look into a report. You can still talk. Reason: {reason}",
    freezeSentenceSafe: "A Game Master has paused your character while they look into a report. You can still talk, and nothing can harm you. Reason: {reason}",
    released: "A moderator released you. You can move again.",
    releasedSafe: "A Game Master released you. You can move again, and nothing can harm you for a few seconds.",
    frozenAct: "You cannot act while a Game Master has paused your character.",
    banTitle: "Banned",
    banUntil: "You are banned until {when}: {reason}",
    banPermanent: "You are banned permanently: {reason}",
  },
  client: {
    session: "Log in again to do that.",
    unreachable: "The server did not answer.",
    failed: "That did not work ({status}).",
    cannotEndOne: "This server cannot end one action yet. Use /lift to end both.",
  },
  desk: {
    notOffered: "You cannot do that.",
    family: "Name a family: letters, digits, hyphens and underscores.",
    reason: "Give a reason of up to 200 characters, on one line.",
    note: "Keep the note to 200 characters, on one line.",
    noneStanding: "No ban or freeze stands.",
    done: {
      kick: "Kicked the {family} family.",
      freeze: "Froze the {family} family for {time}.",
      freezeInCombat: "They were in combat, which is paused.",
      freezeCombatant: "They were flagged as a Combatant.",
      warn: "Warned the {family} family.",
      message: "Message sent to the {family} family.",
      alert: "Flag recorded for the {family} family.",
      banUntil: "Banned the {family} family until {when}.",
      banPermanent: "Banned the {family} family permanently.",
      lift: { one: "Lifted {count} action on the {family} family.", other: "Lifted {count} actions on the {family} family." },
      endedBan: "Ended the ban on the {family} family.",
      endedFreeze: "Ended the freeze on the {family} family.",
      endedBoth: "Ended the ban and the freeze on the {family} family.",
      feedbackpause: "Paused feedback for the {family} family until {when}.",
      feedbackresume: "Feedback is open again for the {family} family.",
      feedbackoff: "Removed feedback access from the {family} family.",
      feedbackon: "Restored feedback access to the {family} family.",
    },
    standing: "{family}: {action} by {issuer} {end}: {reason}",
    until: "until {when}",
    permanent: "permanently",
    alert: "Flag from {by} about the {family} family: {text}",
  },
  usage: {
    kick: "Type /kick, a family and a reason.",
    freeze: "Type /freeze, a family, a length such as 10m if not 15 minutes, and a reason. A freeze lasts 1 to 60 minutes.",
    warn: "Type /warn, a family and a reason.",
    alert: "Type /flag, a family and a note for the staff.",
    ban: "Type /ban, a family, a length (30m, 12h, 7d or perm) and a reason. A ban lasts up to 30 days.",
    lift: "Type /lift, a family and a reason. It ends the family's ban and its freeze; /unban or /unfreeze ends one.",
    unban: "Type /unban, a family and a reason. It ends the family's ban and leaves a freeze standing.",
    unfreeze: "Type /unfreeze, a family and a reason. It ends the family's freeze and leaves a ban standing.",
    feedbackpause: "Type /feedbackpause, a family, a length (30m, 12h or 7d) and a reason. A pause lasts up to 30 days.",
    feedbackresume: "Type /feedbackresume, a family and a reason.",
    feedbackoff: "Type /feedbackoff, a family and a reason. It removes the family's feedback until it is restored.",
    feedbackon: "Type /feedbackon, a family and a reason.",
    actions: "Type /actions alone to list the bans and freezes that stand.",
    gm: "Type /gm alone to open the Moderation window.",
  },
  window: {
    title: "Moderation",
    tabsLabel: "Moderation",
    tabs: { online: "Online", player: "Player", inforce: "In force", history: "History", feedback: "Feedback", staff: "Staff" },
    tabCounted: "{tab}, {count}",
    staff: {
      heading: "Staff accounts",
      family: "Family",
      roles: "Roles",
      wears: "Badge shown",
      designated: "Designated",
      none: "none",
      hidden: "{type}, hidden",
      reading: "Reading the staff accounts.",
      empty: "No account holds a staff role.",
      more: "Showing the first {count} accounts.",
      readAgain: "Read again",
      pick: "Pick an account in the list to set which badge it shows.",
      wornLabel: "Badge worn",
      holds: "{family} holds these types now. Their powers stay the union of their roles.",
      set: "Set designation",
      clear: "Clear designation",
      saved: "Saved. {family} now wears {type}.",
      cleared: "Cleared. {family} wears the default again.",
      faults: {
        refused: "That account no longer holds that type. The list was read again.",
        missing: "No account holds that family now. The list was read again.",
        forbidden: "Your roles do not allow this.",
        session: "Your session has ended. Log in again.",
        rate: "Too many changes. Try again in a minute.",
        failed: "The designation could not be saved. Try again.",
      },
      readFaults: {
        failed: "The staff accounts could not be read. Try again.",
        forbidden: "Your roles do not allow this.",
        session: "Your session has ended. Log in again.",
        rate: "Too many reads. Try again in a minute.",
      },
      roleNames: {
        support: "Support",
        moderator: "Moderator",
        administrator: "Administrator",
        event: "Event",
        builder: "Builder",
        designer: "Designer",
        lead: "Lead",
        guide: "Guide",
        guideLead: "Guide leader",
        supportLead: "Support leader",
        eventLead: "Events leader",
        builderLead: "Builder leader",
        designerLead: "Designer leader",
      },
    },
    pickTitle: "Choose a player",
    pickWide: "Pick someone in the list on the left, or search by character or family. Actions apply to the whole family.",
    pickWideTo: "Pick someone in the list on the left, or search by character or family, to {purpose}. Actions apply to the whole family.",
    pickNarrow: "Pick someone on the Online tab. Actions apply to the whole family.",
    pickNarrowTo: "Pick someone on the Online tab to {purpose}. Actions apply to the whole family.",
    pickFeedback: "see their feedback access",
    closeFile: "Close file",
    lookupCapped: "Showing {shown} of {total}. Narrow the search.",
    hitFamily: "Family",
    hitCharacter: "Character of the {family} family",
    offline: "Offline",
    notOnline: "Not online",
    inMap: "{first} in {map}",
    reading: "Reading the family",
    missing: "No family has that name.",
    unread: "That family could not be read. Try again.",
    inForceOn: "In force on {family}",
    nothingStands: "Nothing stands against this family.",
    feedbackLink: "Feedback tab",
    feedbackShort: { paused: "Feedback paused until {when}", off: "Feedback removed" },
    what: { ban: "Banned", freeze: "Frozen" },
    familyWhat: "{family}: {what}",
    permanent: "Permanent",
    left: "{time} left",
    until: "until {when}",
    leftUntil: "{left}, {until}",
    byRead: "By {issuer}. Player read: “{reason}”",
    end: { ban: "End ban", freeze: "End freeze" },
    cannotEnd: "That is not yours to end: it was set by, or stands on, staff of your standing or higher.",
    actWhole: "Act on {family}: the whole family",
    act: "Act on {family}",
    actions: "Actions on the {family} family",
    cannotAct: "You cannot act on that family.",
    offlineActs: "No character is online. Kick, Freeze and Warn wait until one is.",
    notes: {
      warn: "{family} sees a warning with your message and must acknowledge it before playing on.",
      kick: "Every character of the family leaves the world now. They can log in again.",
      freeze: "Their characters stay where they are and every action is refused until the freeze ends, or you end it.",
      ban: "Every character of the family is logged out and cannot log in until the ban ends.",
      alert: "A note to staff only. Every GM online sees it with {family}'s name and yours; {family} is not told.",
    },
    go: {
      warn: "Warn {family}",
      kick: "Kick {family}",
      freeze: "Freeze for {length}",
      ban: "Ban for {length}…",
      banPermanent: "Ban permanently…",
      banConfirm: "Ban for {length}",
      banPermanentConfirm: "Ban permanently",
      alert: "Send flag",
    },
    length: "Length",
    lengthIn: "{length}, {unit}",
    lengths: { minutes: "{minutes} min", hour: "1 hour", days: { one: "{count} day", other: "{count} days" } },
    or: "or",
    units: { freeze: "min, 1 to 60", ban: "days, up to 30", feedbackpause: "days, up to 30" },
    presets: {
      freeze: { min5: "5 min", min15: "15 min", min30: "30 min", min60: "60 min" },
      ban: { day1: "1 day", days3: "3 days", days7: "7 days", days30: "30 days", permanent: "Permanent" },
      feedbackpause: { hour1: "1 hour", day1: "1 day", days7: "7 days", days30: "30 days" },
    },
    endsAt: "Ends {when}.",
    endsNever: "It does not end.",
    lengthOff: "That length is not one this action takes.",
    playerReads: "{family} reads this",
    playerHint: "Shown word for word in their notice.",
    staffOnly: "Staff only",
    staffHint: "Never shown to the player.",
    flagTitle: "Flag to staff",
    recordedAs: "Recorded as {family}",
    recordedWaiting: "Recording: waiting for the server.",
    cancel: "Cancel",
    tryAgain: "Try again",
    recording: "Recording",
    refused: "The server said no: {sentence} Nothing was recorded.",
    done: { kick: "Kick recorded", freeze: "Freeze recorded", warn: "Warning recorded", message: "Message sent", alert: "Flag recorded", ban: "Ban recorded", lift: "Ended", feedbackpause: "Feedback paused", feedbackresume: "Feedback resumed", feedbackoff: "Feedback removed", feedbackon: "Feedback restored" },
    seeHistory: "See it in History",
    confirmTitle: "Ban the {family} family?",
    confirmWho: "Who",
    confirmWhat: "What",
    confirmLong: "How long",
    confirmFamily: "The {family} family",
    confirmFamilyFirsts: "The {family} family: {firsts}",
    confirmBoth: "{first} and {second}",
    confirmBan: "Ban. Every character is logged out and cannot log in.",
    confirmUntil: "{length}, until {when}",
    confirmReads: "{family} reads",
    confirmNote: "Staff note (not shown to {family}): {note}",
    confirmEnd: "End it any time from In force.",
    back: "Back",
    endTitle: { ban: "End the ban?", freeze: "End the freeze?" },
    endFreezeBody: "The {family} family can act again at once. {left} were left.",
    endBanBody: "The {family} family can log in again at once.",
    endReason: "Reason, staff only",
    endHint: { ban: "Recorded in History.", freeze: "Recorded in History. {family} is told only that the freeze has ended." },
    inforceEmptyTitle: "Nothing in force",
    inforceEmpty: "No ban or freeze stands on any family.",
    inforceOrder: "Soonest to end first",
    inforceNote: "Select a row to open that family on the Player tab, online or not. Warnings and kicks are moments, not states: they are in History.",
    open: "Open {family} on the Player tab",
    historyOrder: "Newest first",
    historyLabel: "Moderation history",
    today: "Today {time}",
    yesterday: "Yesterday {time}",
    columns: { when: "When", family: "Family", what: "What", by: "By" },
    historyEmptyTitle: "Nothing recorded yet",
    historyEmpty: "Every warning, kick, freeze, ban, flag and end appears here with who did it.",
    readQuote: "{family} read: “{reason}”",
    staffNote: "Staff note: {note}",
    more: "Show more",
    history: {
      kick: "Kicked",
      warn: "Warned",
      alert: "Flag",
      lift: "Ended",
      freeze: "Frozen, {length}",
      ban: "Banned, {length}",
      banPermanent: "Banned, permanent",
    },
    feedbackHeading: "Feedback access",
    feedbackFor: "for the {family} family",
    feedbackStates: { open: "Allowed", off: "Removed" },
    feedbackPausedUntil: "Paused until {when}",
    feedbackOpen: "{family} can send bug reports from the game menu.",
    feedbackSees: "{family} sees, on opening Feedback",
    feedbackNormal: "The normal Feedback form.",
    quoted: "“{text}”",
    feedbackButtons: { feedbackpause: "Pause", feedbackresume: "Resume", feedbackoff: "Remove", feedbackon: "Restore" },
    feedbackGo: { feedbackpause: "Pause for {length}", feedbackresume: "Resume feedback", feedbackoff: "Remove feedback", feedbackon: "Restore feedback" },
    feedbackReason: "Reason, staff only",
    feedbackNote: "Separate from bans and freezes: it never touches chat or play. Every change asks for a staff-only reason; only Pause asks for a length.",
    legend: "Marks",
  },
  actions: { kick: "Kick", freeze: "Freeze", warn: "Warn", message: "Message", alert: "Flag", ban: "Ban", lift: "Lift", feedbackpause: "Pause feedback", feedbackresume: "Resume feedback", feedbackoff: "Remove feedback access", feedbackon: "Restore feedback access" },
  badge: {
    guide: "Guide",
    guideYears: { one: "Guide, {count} year", other: "Guide, {count} years" },
    guideLead: "Guide leader",
    support: "Player Support",
    supportLead: "Player Support, team leader",
    events: "Events",
    eventsLead: "Events, team leader",
    builder: "Builder",
    builderLead: "Builder, team leader",
    designer: "Designer",
    designerLead: "Designer, team leader",
    administrator: "Administrator",
    administratorLead: "Administrator, team leader",
    hidden: "{badge}, hidden",
  },
  online: {
    heading: "Online players",
    search: "Search",
    notOnline: "Not online",
    notOnlineNone: "No one who is not online matches that.",
    grouped: "{name}, {group}",
    here: "Here: {header}",
    marks: { you: "You", staff: "Staff", frozen: "Frozen", muted: "Muted" },
    reading: "Reading who is online",
    noMatch: "No one online matches that.",
    nobody: "No one is online.",
    capped: "Search to narrow the list.",
    failed: "The list of players could not be read.",
    rate: "Too many requests; try again in a minute.",
    session: "Log in again to do that.",
    forbidden: "You cannot do that.",
  },
  refused: {
    session: "Log in again to do that.",
    forbidden: "You cannot do that.",
    standing: "You cannot act on that family.",
    family: "No family has that name.",
    own: "That is your own family.",
    offline: "That family is not in the world.",
    active: "That family is already frozen.",
    none: "That family has no ban or freeze to lift.",
    protected: "That family is protected and cannot be acted on.",
    duration: "That action takes no length.",
    body: "That request was not understood.",
    reason: "Give a reason of up to 200 characters, on one line.",
    note: "Keep the note to 200 characters, on one line.",
    rate: "Too many actions; try again in a minute.",
    unavailable: "That could not be saved. Try again.",
  },
  refusedFor: {
    liftStanding: "That is not yours to end: it was set by, or stands on, staff of your standing or higher.",
    liftGone: "That action no longer stands.",
    noBan: "That family has no ban to end.",
    noFreeze: "That family has no freeze to end.",
    banActive: "That family is already banned. Lift the ban first.",
    banDuration: "A ban lasts up to 30 days. Only a lead bans permanently.",
    freezeDuration: "A freeze lasts 1 to 60 minutes.",
    pauseDuration: "A feedback pause lasts 1 minute to 30 days.",
    feedbackOffActive: "That family's feedback is already turned off.",
    feedbackOnNone: "That family's feedback is not turned off.",
    feedbackResumeNone: "That family's feedback is not paused.",
    guideMinute: "You can flag two players a minute. Wait a moment, then try again.",
    guideDay: "You have flagged as many players as you can today. Try again tomorrow.",
    guideFamily: "You flagged that family a moment ago. Give the staff ten minutes before you flag them again.",
  },
  route: {
    method: "Use {methods}.",
    either: "{first} or {second}",
    contentType: "Send the body as application/json.",
    familyUnread: "That family could not be read. Try again.",
    onlineUnread: "The list of players could not be read.",
    lookupUnread: "That lookup could not be read. Try again.",
    badgeUnread: "Your badge could not be changed. Try again.",
    designationUnread: "The designation could not be changed. Try again.",
    designationNotHeld: "That account does not hold a role of that type.",
    skyUnread: "The sky could not be changed. Try again.",
    staffUnread: "The staff accounts could not be read. Try again.",
  },
} as const;
